<template>
  <div class="catalog-wizard">
    <!-- Trilha de passos -->
    <div class="catalog-wizard-steps" role="tablist">
      <button
        v-for="(step, index) in steps"
        :key="step.key"
        type="button"
        class="catalog-wizard-step"
        :class="{
          'is-active': step.key === current,
          'is-done': isDone(step.key) && step.key !== current,
          'is-locked': !isReachable(step.key)
        }"
        :disabled="!isReachable(step.key)"
        @click="goTo(step.key)">
        <span class="catalog-wizard-step-badge">
          <feather-icon
            v-if="isDone(step.key) && step.key !== current"
            icon="CheckIcon"
            svgClasses="w-4 h-4"/>
          <template v-else>{{ index + 1 }}</template>
        </span>
        <span class="catalog-wizard-step-text">
          <span class="catalog-wizard-step-title">{{ step.title }}</span>
          <small v-if="step.hint" class="catalog-wizard-step-hint">{{ step.hint }}</small>
        </span>
      </button>
    </div>

    <!-- Conteúdo do passo -->
    <div class="catalog-wizard-body">
      <slot/>
    </div>

    <!-- Rodapé de navegação -->
    <div class="catalog-wizard-footer">
      <div class="catalog-wizard-footer-info">
        <slot name="footer-info"/>
      </div>
      <div class="catalog-wizard-footer-actions">
        <b-button
          v-if="currentIndex > 0"
          variant="outline-secondary"
          :disabled="busy"
          @click="$emit('prev')">
          <feather-icon icon="ArrowLeftIcon" class="mr-50"/>
          <span class="align-middle">{{ $t('catalog.wizard.back') }}</span>
        </b-button>
        <slot name="footer-actions">
          <b-button
            v-if="!isLast"
            variant="primary"
            :disabled="nextDisabled || busy"
            @click="$emit('next')">
            <span class="align-middle">{{ nextLabel || $t('catalog.wizard.continue') }}</span>
            <feather-icon icon="ArrowRightIcon" class="ml-50"/>
          </b-button>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'

/**
 * Casca do passo a passo de cadastro (aula, plano, espaço).
 *
 * Ela só desenha e navega; quem decide se pode avançar é o formulário dono
 * (validação, save por passo). Em modo edição (`freeNavigation`) todos os
 * passos ficam clicáveis; no cadastro novo só os já concluídos e o atual.
 */
export default {
  name: 'WizardShell',
  components: { BButton },
  props: {
    /** [{ key, title, hint? }] */
    steps: { type: Array, required: true },
    /** key do passo atual */
    current: { type: String, required: true },
    /** keys já concluídas (habilita voltar por clique) */
    doneKeys: { type: Array, default: () => [] },
    /** edição: navegar livre entre passos */
    freeNavigation: { type: Boolean, default: false },
    nextDisabled: { type: Boolean, default: false },
    nextLabel: { type: String, default: null },
    busy: { type: Boolean, default: false },
  },
  computed: {
    currentIndex() {
      return this.steps.findIndex(step => step.key === this.current)
    },
    isLast() {
      return this.currentIndex === this.steps.length - 1
    },
  },
  methods: {
    isDone(key) {
      return this.doneKeys.includes(key)
    },
    isReachable(key) {
      if (this.freeNavigation) return true
      return key === this.current || this.isDone(key)
    },
    goTo(key) {
      if (key !== this.current && this.isReachable(key)) {
        this.$emit('goto', key)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.catalog-wizard-steps {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.catalog-wizard-step {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 0.5rem;
  padding: 0.45rem 0.75rem;
  cursor: pointer;
  text-align: left;
  flex: 0 0 auto;
  color: inherit;

  &.is-locked {
    cursor: default;
    opacity: 0.5;
  }

  &:not(.is-locked):hover {
    background: rgba(115, 103, 240, 0.08);
  }

  &.is-active {
    border-color: rgba(115, 103, 240, 0.45);
    background: rgba(115, 103, 240, 0.1);
  }
}

.catalog-wizard-step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.08);
  font-weight: 600;
  font-size: 0.8rem;
  flex: 0 0 auto;

  .is-active & {
    background: #7367f0;
    color: #fff;
  }

  .is-done & {
    background: rgba(40, 199, 111, 0.18);
    color: #28c76f;
  }
}

.catalog-wizard-step-text {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.catalog-wizard-step-title {
  font-weight: 600;
  font-size: 0.95rem;
  white-space: nowrap;
}

.catalog-wizard-step-hint {
  opacity: 0.65;
  white-space: nowrap;
}

.catalog-wizard-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  flex-wrap: wrap;
}

.catalog-wizard-footer-actions {
  display: flex;
  gap: 0.75rem;
  margin-left: auto;
}
</style>
